import { __rest } from "tslib";
import { isNil } from 'ramda';
import { RuleFilterValueType, RuleType, } from '../models';
import { refineModelForRequest } from '@/client/formatters/utils';
import { SearchForRetail } from '@/constants';
import { DEFAULT_RELEVANCY } from '../biasing-strategy/biasing-strategy.constants';
const withDefaultValue = (value, fallback) => {
    if (isNil(value)) {
        return fallback;
    }
    return value;
};
const normalizeProductIdFilter = (productIdFilter) => (Object.assign(Object.assign({}, productIdFilter), { includedProductIds: withDefaultValue(productIdFilter === null || productIdFilter === void 0 ? void 0 : productIdFilter.includedProductIds, []), excludedProductIds: withDefaultValue(productIdFilter === null || productIdFilter === void 0 ? void 0 : productIdFilter.excludedProductIds, []), includeOutOfStock: withDefaultValue(productIdFilter === null || productIdFilter === void 0 ? void 0 : productIdFilter.includeOutOfStock, true) }));
const hasProductsInBucket = (buckets) => Boolean(buckets === null || buckets === void 0 ? void 0 : buckets.some(({ products }) => products.length > 0));
const formatSearchFilters = (searchFilters) => {
    if (isNil(searchFilters)) {
        return undefined;
    }
    return searchFilters.map((value) => ({ value }));
};
const formatValueFilter = (_a) => {
    var { valueType } = _a, filter = __rest(_a, ["valueType"]);
    return (Object.assign({ type: valueType }, filter));
};
const formatRange = (range) => {
    const { low, high, exclude } = range, rangeData = __rest(range, ["low", "high", "exclude"]);
    return Object.assign(Object.assign({}, rangeData), { range: Object.assign({ low,
            high }, (!isNil(exclude) ? { exclude } : {})) });
};
const formatAttributeFilter = (_a) => {
    var { values, numberValues } = _a, filter = __rest(_a, ["values", "numberValues"]);
    if (filter.type === RuleFilterValueType.Textual) {
        return [...values].map((value) => (Object.assign({ value }, filter)));
    }
    return [...numberValues].map((numberValue) => (Object.assign({ numberValue }, filter)));
};
const formatAttributeFilters = (attributeFilters) => attributeFilters.reduce((result, filters) => {
    const parsedFilter = { rangeFilters: [], valueFilters: [] };
    filters.forEach((_a) => {
        var { filterType, low, high } = _a, filteredData = __rest(_a, ["filterType", "low", "high"]);
        if (filterType === SearchForRetail.FilterType.RANGE) {
            parsedFilter.rangeFilters.push(formatRange({
                field: filteredData.field,
                low,
                high,
                exclude: filteredData.exclude,
            }));
        }
        if (filterType === SearchForRetail.FilterType.FILTERED_BY) {
            parsedFilter.valueFilters = [
                ...parsedFilter.valueFilters,
                ...formatAttributeFilter(filteredData),
            ];
        }
    });
    return ([...result, parsedFilter]);
}, []);
const formatResultManagement = (filters) => filters.reduce((resultManagementData, filter) => {
    const { filterType } = filter, filteredData = __rest(filter, ["filterType"]);
    if (filterType === SearchForRetail.FilterType.EXCLUSION) {
        const formattedFilter = formatValueFilter(filteredData);
        resultManagementData.valueFilters.push(Object.assign(Object.assign({}, formattedFilter), { exclude: true }));
    }
    if (filterType === SearchForRetail.FilterType.RANGE) {
        const formattedFilter = formatRange(filteredData);
        resultManagementData.rangeFilters.push(formattedFilter);
    }
    if (filterType === SearchForRetail.FilterType.SEARCH) {
        resultManagementData.searchFilters.push(filteredData);
    }
    if (filterType === SearchForRetail.FilterType.FILTERED_BY) {
        const formattedFilter = formatValueFilter(filteredData);
        resultManagementData.valueFilters.push(formattedFilter);
    }
    return resultManagementData;
}, {
    searchFilters: [],
    valueFilters: [],
    rangeFilters: [],
});
const formatRuleTrigger = (triggerSet) => triggerSet.reduce((formattedTriggers, trigger) => {
    const { triggerType } = trigger, triggerData = __rest(trigger, ["triggerType"]);
    const triggerKind = SearchForRetail.triggerMap[triggerType];
    const formattedTriggerData = triggerType === SearchForRetail.TriggerType.NAVIGATION_RANGE
        ? formatRange(triggerData)
        : triggerData;
    const canIncludeTriggerType = !!triggerType && triggerType !== SearchForRetail.TriggerType.CUSTOM_PARAMETER;
    formattedTriggers[triggerKind].push(Object.assign(Object.assign({}, formattedTriggerData), { type: canIncludeTriggerType ? triggerType : undefined }));
    return formattedTriggers;
}, {
    queryPatternTriggers: [],
    selectedRefinementTriggers: [],
    customParameterTriggers: [],
});
export const formatRuleVariant = (_a) => {
    var { resultManagementRefinements, biasingStrategy, pinnedProducts, boostedProductBuckets, buriedProductBuckets, name, winner, active, variantTriggerPercentage, attributeFilters, searchFilters } = _a, model = __rest(_a, ["resultManagementRefinements", "biasingStrategy", "pinnedProducts", "boostedProductBuckets", "buriedProductBuckets", "name", "winner", "active", "variantTriggerPercentage", "attributeFilters", "searchFilters"]);
    const normalizedBoostedProductBuckets = withDefaultValue(boostedProductBuckets, []);
    const normalizedPinnedProducts = withDefaultValue(pinnedProducts, []);
    const normalizedAttributeFilters = withDefaultValue(attributeFilters, []);
    const normalizedBuriedProductBuckets = withDefaultValue(buriedProductBuckets, []);
    const normalizedSearchFilters = formatSearchFilters(searchFilters);
    const ruleVariant = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, model), formatResultManagement(resultManagementRefinements)), { biasingProfileName: biasingStrategy === DEFAULT_RELEVANCY ? undefined : biasingStrategy, pinnedProducts: normalizedPinnedProducts, attributeFilters: formatAttributeFilters(normalizedAttributeFilters) }), (hasProductsInBucket(normalizedBoostedProductBuckets) ? { boostedProductBuckets: normalizedBoostedProductBuckets } : {})), (!isNil(normalizedSearchFilters) ? { searchFilters: normalizedSearchFilters } : {})), (hasProductsInBucket(normalizedBuriedProductBuckets) ? { buriedProductBuckets: normalizedBuriedProductBuckets } : {})), { productIdFilter: normalizeProductIdFilter(model.productIdFilter), pinnedRefinements: withDefaultValue(model.pinnedRefinements, []), removedGlobalFacets: withDefaultValue(model.removedGlobalFacets, []), hiddenDynamicFacets: withDefaultValue(model.hiddenDynamicFacets, []), ruleFacets: withDefaultValue(model.ruleFacets, []), includeDynamicFacets: withDefaultValue(model.includeDynamicFacets, true), hiddenRefinements: withDefaultValue(model.hiddenRefinements, []) });
    return {
        name,
        winner,
        active,
        variantTriggerPercentage,
        ruleVariant,
    };
};
export const formatRule = (_a) => {
    var { areaId, dateRange = { activeFrom: undefined, activeTo: undefined }, triggerSets = [], activeHoursEnabled, copyable = true, ruleVariants = [], inactiveRuleVariants = [], type } = _a, model = __rest(_a, ["areaId", "dateRange", "triggerSets", "activeHoursEnabled", "copyable", "ruleVariants", "inactiveRuleVariants", "type"]);
    const requestModel = refineModelForRequest(model);
    const experimentRuleVariants = [...ruleVariants, ...inactiveRuleVariants].map(formatRuleVariant);
    const mainVariant = experimentRuleVariants.find(({ active }) => active);
    const ruleDTO = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, requestModel), (activeHoursEnabled && {
        activeFrom: dateRange.activeFrom,
        activeTo: dateRange.activeTo,
    })), { triggerSets: triggerSets.map(formatRuleTrigger) }), mainVariant === null || mainVariant === void 0 ? void 0 : mainVariant.ruleVariant), { areaId,
        activeHoursEnabled, enabled: true, copyable,
        type }), (type === RuleType.Experiment ? { variants: experimentRuleVariants } : undefined));
    return ruleDTO;
};
export const formatAnalyticsCellNumber = (num) => {
    if (num >= 1000) {
        return `${(num / 1000).toFixed(1)}K`;
    }
    return parseFloat(num.toFixed(1)).toString();
};
